import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import {
  ApiException,
  type UserAuthorization,
  getUserAuthorization as apiGetUserAuthorization
} from "@/helpers/apiDashboardWrappers";

export const useUserAuthorizationStore = defineStore('userAuthorization', () => {
  const isDone = ref(false);
  const inProgress = ref(false);
  const lastAuthorization = ref<UserAuthorization>();
  const authorizationError = ref<ApiException>();
  const getUserAuthorizationPromise = ref<Promise<UserAuthorization | undefined>>();

  async function getCurrentAuthorization() {
    if (!inProgress.value) {
      return lastAuthorization.value;
    }

    try {
      return await getUserAuthorizationPromise.value;
    }
    catch {
      return undefined;
    }
  }

  async function refreshAuthorization() {
    getUserAuthorizationPromise.value = apiGetUserAuthorization();
    await loadUserAuthorization();
  }

  async function loadUserAuthorization(): Promise<void> {
    inProgress.value = true;

    try {
      lastAuthorization.value = await getUserAuthorizationPromise.value;
      authorizationError.value = undefined;
    }
    catch (err) {
      const apiError = ApiException.isApiException(err) ? err : undefined;
      lastAuthorization.value = undefined;
      if (apiError?.status === 401)
        throw err;

      authorizationError.value = apiError ?? new ApiException('Unexpected authorization error', 500, '', {}, undefined);
    }
    finally {
      isDone.value = true;
      inProgress.value = false;
    }
  }

  return {
    refreshAuthorization,
    getCurrentAuthorization,
    authorizationIsDone: computed(() => isDone.value),
    authorizationInProgress: computed(() => inProgress.value),
    lastAuthorization: computed(() => lastAuthorization.value),
    authorizationError: computed(() => authorizationError.value),
    currentAuthorization: computed(() => inProgress.value ? undefined : lastAuthorization.value),
    permissions: computed(() => lastAuthorization.value?.permissions ?? [])
  };
});
